import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'

import { clusterRepository } from '../../infrastructure/clusterRepo'
import type { UpdateClusterRequest } from '../../domain/types'

type UpdateClusterParams = {
  id: string
  body: UpdateClusterRequest
}

export function useUpdateCluster() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, body }: UpdateClusterParams) => {
      const result = await clusterRepository.update(id, body)

      if (!result.response.ok) {
        // Every backend error body is {"error": "<string>"}; the spec does
        // not model it, so it is read untyped.
        const errorBody = result.error as unknown as
          | { error?: string }
          | undefined
        const message =
          errorBody?.error ||
          `Cluster could not be updated (HTTP ${result.response.status})`

        // A subnet that is being deleted keeps its VLAN until the cluster
        // releases it, and it is refused against here like any other holder —
        // the backend's message does not say so.
        if (body.transportVlan !== undefined && /vlan/i.test(message)) {
          throw new Error(
            `${message} A subnet in Deleting still holds its VLAN — check the network list for one on this VLAN.`,
          )
        }

        throw new Error(message)
      }

      return { id, body }
    },

    onError: (error: Error) => {
      toast.error(error.message)
    },

    onSuccess: () => {
      toast.success('Cluster was updated')
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['clusters'] })
    },
  })
}
